import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/apiClient";
import { formatIsoDateShort } from "@/lib/bookingDates";
import { billingIntervalLabel } from "@/lib/contractBilling";
import { contractTypeLabel } from "@/lib/contractType";
import {
  ContractCreditBadge,
  contractAmountLabel,
  contractStatusLabel,
} from "@/pages/contracts/contractDisplay";
import type { Contract } from "@/types/camping";

export function GuestContractsPanel({
  guestId,
  active,
}: {
  guestId: string;
  active: boolean;
}) {
  const navigate = useNavigate();
  const [contracts, setContracts] = useState<Contract[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    if (!guestId) return;
    setLoading(true);
    setError(null);
    api
      .get<Contract[]>(`/api/v1/contracts?guest_id=${encodeURIComponent(guestId)}`)
      .then(setContracts)
      .catch((err: unknown) => {
        setContracts([]);
        setError(err instanceof Error ? err.message : "Verträge konnten nicht geladen werden.");
      })
      .finally(() => setLoading(false));
  }, [guestId]);

  useEffect(() => {
    if (!active) return;
    load();
  }, [active, load]);

  if (!active) return null;

  return (
    <section className="rounded-xl border border-separator bg-surface-secondary/40 p-4">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h3 className="font-medium text-label">Verträge</h3>
        {!loading && !error ? (
          <span className="text-sm text-label-secondary">
            {contracts.length} Vertrag{contracts.length === 1 ? "" : "e"}
          </span>
        ) : null}
      </div>

      {loading ? (
        <p className="mt-3 text-sm text-label-secondary">Verträge werden geladen…</p>
      ) : null}
      {error ? <p className="mt-3 text-sm text-status-red">{error}</p> : null}

      {!loading && !error && contracts.length === 0 ? (
        <p className="mt-3 text-sm text-label-secondary">Keine Verträge für diesen Gast.</p>
      ) : null}

      {!loading && !error && contracts.length > 0 ? (
        <ul className="mt-4 space-y-2">
          {contracts.map((contract) => (
            <li key={contract.id}>
              <button
                type="button"
                className="w-full rounded-lg border border-separator bg-surface-primary/40 p-3 text-left transition hover:bg-fill-tertiary/50"
                onClick={() => navigate("/contracts")}
              >
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-medium">{contractTypeLabel(contract.contract_type)}</span>
                  <span className="text-sm text-label-secondary">
                    {contractStatusLabel(contract.status)}
                  </span>
                  <ContractCreditBadge contract={contract} />
                </div>
                <p className="mt-1 text-sm text-label-secondary">
                  {formatIsoDateShort(contract.start_date)}
                  {contract.end_date ? ` – ${formatIsoDateShort(contract.end_date)}` : " – unbefristet"}
                  {contract.pitch_code ? ` · Platz ${contract.pitch_code}` : ""}
                </p>
                <p className="mt-0.5 text-xs text-label-tertiary">
                  {billingIntervalLabel(contract.billing_interval)}
                  {" · "}
                  {contractAmountLabel(contract)}
                  {contract.booking_number ? ` · Buchung ${contract.booking_number}` : ""}
                  {contract.superseded_on ? ` · abgelöst zum ${formatIsoDateShort(contract.superseded_on)}` : ""}
                </p>
                {contract.special_terms?.trim() ? (
                  <p className="mt-1 line-clamp-2 text-xs text-label-secondary" title={contract.special_terms}>
                    <span className="font-medium text-status-orange">Sonderkonditionen: </span>
                    {contract.special_terms}
                  </p>
                ) : null}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </section>
  );
}
